import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminApi, Client, CreateClientRequest, ClientTier } from '../api/admin';
import { Plus, X, UserCheck, ChevronRight, Search, Filter, Crown } from 'lucide-react';
import ClientDetailsModal from '../components/ClientDetailsModal';
import { useSelectedClientStore } from '../store/selectedClientStore';
import { formatPublicIdDisplay } from '../utils/formatPublicId';
import { formatApplicationType } from '../utils/formatApplicationType';
import { getClientDisplayName } from '../utils/clientDisplay';

function TierBadge({
  tier,
  tierId,
  tierNameById,
  showLabel = true
}: {
  tier?: ClientTier;
  tierId?: string;
  tierNameById?: string;
  showLabel?: boolean;
}) {
  if (!tier && !tierId) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
        No Tier
      </span>
    );
  }

  if (!tier && tierId) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700">
        {showLabel ? (tierNameById || 'Assigned') : 'Assigned'}
      </span>
    );
  }
  const resolvedTier = tier as ClientTier;

  // Default colors by code, or use tier.color if available
  const getDefaultStyles = (code: string) => {
    switch (code.toUpperCase()) {
      case 'STANDARD': return { bg: 'bg-gray-100', text: 'text-gray-700' };
      case 'SILVER': return { bg: 'bg-slate-200', text: 'text-slate-700' };
      case 'GOLD': return { bg: 'bg-yellow-100', text: 'text-yellow-700' };
      case 'PLATINUM': return { bg: 'bg-indigo-100', text: 'text-indigo-700' };
      case 'VIP': return { bg: 'bg-purple-100', text: 'text-purple-700' };
      default: return { bg: 'bg-gray-100', text: 'text-gray-700' };
    }
  };

  const isVIP = resolvedTier.code.toUpperCase() === 'VIP';
  const styles = getDefaultStyles(resolvedTier.code);

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${styles.bg} ${styles.text}`}
          style={resolvedTier.color ? { backgroundColor: resolvedTier.color + '20', color: resolvedTier.color } : undefined}>
      {isVIP && <Crown className="w-3 h-3" />}
      {showLabel && resolvedTier.name}
    </span>
  );
}

function sortClientsNewestFirst(items: Client[]): Client[] {
  return [...items].sort((a, b) => {
    const tb = b.createdAt ? new Date(b.createdAt).getTime() : Number.NEGATIVE_INFINITY;
    const ta = a.createdAt ? new Date(a.createdAt).getTime() : Number.NEGATIVE_INFINITY;
    if (tb !== ta) return tb - ta;
    return String(b.id).localeCompare(String(a.id)); // tie-break stable by id desc if available
  });
}

/** Portal client profile status string (same as C# enum: Active, Unactive, …). */
function portalProfileStatusBadgeClass(status: string | undefined): string {
  const s = (status || '').trim();
  switch (s) {
    case 'Active':
      return 'bg-green-100 text-green-800';
    case 'Unactive':
      return 'bg-amber-100 text-amber-900';
    case 'Restricted':
      return 'bg-orange-100 text-orange-900';
    case 'Closed':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-700';
  }
}

function ClientsPage() {
  const navigate = useNavigate();
  const { selectedClient: globalSelectedClient, setSelectedClient: setGlobalSelectedClient, clearSelectedClient } = useSelectedClientStore();

  const [allClients, setAllClients] = useState<Client[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [tierLookup, setTierLookup] = useState<Record<string, ClientTier>>({});
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<'' | 'BUSINESS' | 'PRIVATE'>('');
  const [modalOpen, setModalOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [formData, setFormData] = useState<CreateClientRequest>({
    companyName: '',
    email: '',
    password: '',
    phoneNumber: '',
    clientType: 'business',
    firstName: '',
    lastName: '',
  });

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const loadTiers = async () => {
      try {
        const response = await adminApi.getAllTiers(true);
        const map = (response.data || []).reduce<Record<string, ClientTier>>((acc, t) => {
          acc[t.id] = t;
          return acc;
        }, {});
        setTierLookup(map);
      } catch {
        // non-blocking for clients list
      }
    };

    const fetchClients = async () => {
      try {
        setLoading(true);
        const response = await adminApi.getClients();
        const data = sortClientsNewestFirst(response.data ?? []);
        setAllClients(data);
        setClients(data);
      } catch (err: any) {
        setError(err.message || 'Failed to fetch clients');
      } finally {
        setLoading(false);
      }
    };

    loadTiers();
    fetchClients();
  }, []);

  const handleSearch = useCallback((q: string) => {
    setSearchQuery(q);
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!q.trim()) {
      setClients(allClients);
      setSearching(false);
      return;
    }

    setSearching(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const resp = await adminApi.searchClients(q.trim());
        const raw: any[] = Array.isArray(resp.data)
          ? resp.data
          : (resp.data as any)?.data ?? [];
        setClients(sortClientsNewestFirst(raw as Client[]));
      } catch {
        const q2 = q.toLowerCase();
        setClients(
          sortClientsNewestFirst(
            allClients.filter(
              (c) =>
                c.email?.toLowerCase().includes(q2) ||
                c.companyName?.toLowerCase().includes(q2) ||
                [c.firstName, c.lastName].filter(Boolean).join(' ').toLowerCase().includes(q2) ||
                (c as unknown as { publicId?: string }).publicId?.toLowerCase().includes(q2)
            )
          )
        );
      } finally {
        setSearching(false);
      }
    }, 300);
  }, [allClients]);

  const filteredClients = useMemo(() => {
    if (!typeFilter) return clients;
    return clients.filter((c) => (c.applicationType ?? '').toUpperCase() === typeFilter);
  }, [clients, typeFilter]);

  const handleCreateClient = async () => {
    try {
      setCreating(true);
      setError('');
      await adminApi.createClient(formData);
      
      // Refresh clients list
      const response = await adminApi.getClients();
      const data = sortClientsNewestFirst(response.data ?? []);
      setAllClients(data);
      setClients(data);
      
      // Close modal and reset form
      setModalOpen(false);
      setFormData({
        companyName: '',
        email: '',
        password: '',
        phoneNumber: '',
        clientType: 'business',
        firstName: '',
        lastName: '',
      });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create client');
    } finally {
      setCreating(false);
    }
  };

  const handleInputChange = (field: keyof CreateClientRequest, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSelectClient = (client: Client, e: React.MouseEvent) => {
    e.stopPropagation();
    const fullName = getClientDisplayName(client) || client.email;
    setGlobalSelectedClient({
      id: client.id,
      publicId: (client as any).publicId,
      email: client.email,
      firstName: client.firstName,
      lastName: client.lastName,
      companyName: client.companyName,
      applicationType: client.applicationType,
      fullName,
    });
    navigate('/accounts');
  };

  const handleTierUpdated = (clientId: string, tierId: string | null, tier?: ClientTier) => {
    const resolvedTier = tierId ? (tier || tierLookup[tierId]) : undefined;
    const patch = (list: Client[]) =>
      list.map((c) =>
        c.id === clientId
          ? {
              ...c,
              tierId: tierId ?? undefined,
              tier: resolvedTier,
            }
          : c
      );

    setAllClients((prev) => patch(prev));
    setClients((prev) => patch(prev));
    setSelectedClient((prev) =>
      prev && prev.id === clientId
        ? { ...prev, tierId: tierId ?? undefined, tier: resolvedTier }
        : prev
    );
  };

  const handleEmailUpdated = useCallback((clientId: string, email: string) => {
    const nextEmail = email.trim().toLowerCase();
    const patch = (list: Client[]) =>
      list.map((c) =>
        c.id === clientId
          ? {
              ...c,
              email: nextEmail,
            }
          : c
      );

    setAllClients((prev) => patch(prev));
    setClients((prev) => patch(prev));
    setSelectedClient((prev) =>
      prev && prev.id === clientId
        ? { ...prev, email: nextEmail }
        : prev
    );

    if (globalSelectedClient?.id === clientId) {
      setGlobalSelectedClient({
        ...globalSelectedClient,
        email: nextEmail,
      });
    }
  }, [globalSelectedClient, setGlobalSelectedClient]);

  if (loading) {
    return (
      <div className="p-6">
        <div className="mb-8">
          <h1 className="text-2xl font-semibold text-gray-900">Clients</h1>
          <p className="text-gray-600">Manage all clients and their accounts</p>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="mb-8">
          <h1 className="text-2xl font-semibold text-gray-900">Clients</h1>
          <p className="text-gray-600">Manage all clients and their accounts</p>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="text-red-600">Error: {error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Clients</h1>
          <p className="text-gray-600">Manage all clients and their accounts</p>
        </div>
        <button
          onClick={() => {
            setFormData((prev) => ({ ...prev, clientType: 'business' }));
            setModalOpen(true);
          }}
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="h-5 w-5 mr-2" />
          Add Client
        </button>
      </div>

      {globalSelectedClient && (
        <div className="mb-6 flex items-center gap-3 px-4 py-3 bg-indigo-50 border border-indigo-200 rounded-lg">
          <UserCheck className="h-5 w-5 text-indigo-600 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-indigo-900">Active client context</p>
            <p className="text-xs text-indigo-600 truncate">
              {globalSelectedClient.fullName}
              {globalSelectedClient.publicId && <span className="ml-2 font-mono">{formatPublicIdDisplay(globalSelectedClient.publicId)}</span>}
              <span className="ml-2 text-indigo-400">{globalSelectedClient.email}</span>
            </p>
          </div>
          <button
            onClick={clearSelectedClient}
            className="text-indigo-400 hover:text-indigo-600 transition-colors"
            title="Clear selection"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
      
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200 flex flex-col gap-4">
          <div className="flex items-center gap-4 flex-wrap">
            <h2 className="text-lg font-medium text-gray-900 flex-shrink-0">
              {searchQuery.trim()
                ? `Results (${filteredClients.length}${typeFilter ? ` · filtered` : ''})`
                : `All Clients (${filteredClients.length}${typeFilter || searchQuery.trim() ? ` of ${allClients.length}` : ''})`}
            </h2>
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              <input
                type="text"
                value={searchQuery}
                onChange={e => handleSearch(e.target.value)}
                placeholder="Search by name, email, company or ID (XC-…)"
                className="w-full pl-9 pr-8 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {searching && (
                <span className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
              )}
              {!searching && searchQuery && (
                <button
                  onClick={() => handleSearch('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
          <div className="flex items-center gap-4 flex-wrap bg-gray-50 -mx-6 -mb-4 px-6 py-3 border-t border-gray-100">
            <div className="flex items-center gap-2">
              <Filter className="h-5 w-5 text-gray-500" />
              <label htmlFor="clients-type-filter" className="text-sm font-medium text-gray-700">
                Client type:
              </label>
            </div>
            <select
              id="clients-type-filter"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value as '' | 'BUSINESS' | 'PRIVATE')}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm bg-white"
            >
              <option value="">All types</option>
              <option value="BUSINESS">Business</option>
              <option value="PRIVATE">Individual</option>
            </select>
            <div className="ml-auto text-sm text-gray-500">
              Showing: {filteredClients.length}
              {!searchQuery.trim() && !typeFilter ? (
                <span className="text-gray-400"> · {allClients.length} total</span>
              ) : null}
            </div>
          </div>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Company
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tier
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email Confirmed
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  2FA
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created At
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Login
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredClients.map((client) => {
                const isActiveContext = globalSelectedClient?.id === client.id;
                return (
                  <tr
                    key={client.id}
                    className={`hover:bg-gray-50 cursor-pointer ${isActiveContext ? 'bg-indigo-50' : ''}`}
                    onClick={() => setSelectedClient(client)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{client.email}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 max-w-[220px] truncate" title={client.companyName || undefined}>
                        {client.companyName?.trim() ? client.companyName : '—'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <div className="text-sm text-gray-900">
                          {client.firstName && client.lastName
                            ? `${client.firstName} ${client.lastName}`
                            : client.firstName || client.lastName || 'N/A'}
                        </div>
                        {isActiveContext && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-700">
                            Selected
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatApplicationType(client.applicationType)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <TierBadge
                        tier={client.tier}
                        tierId={client.tierId}
                        tierNameById={client.tierId ? tierLookup[client.tierId]?.name : undefined}
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${portalProfileStatusBadgeClass(
                          client.status,
                        )}`}
                      >
                        {client.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        client.emailConfirmed
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {client.emailConfirmed ? 'Yes' : 'No'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        client.twoFactorEnabled
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {client.twoFactorEnabled ? 'Yes' : 'No'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {client.createdAt ? new Date(client.createdAt).toLocaleDateString() : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {client.lastLoginAt ? new Date(client.lastLoginAt).toLocaleString() : 'Never'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={(e) => handleSelectClient(client, e)}
                        className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                          isActiveContext
                            ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                            : 'bg-gray-100 text-gray-700 hover:bg-indigo-50 hover:text-indigo-700'
                        }`}
                      >
                        <UserCheck className="h-3.5 w-3.5" />
                        {isActiveContext ? 'Active' : 'Select'}
                        {!isActiveContext && <ChevronRight className="h-3 w-3" />}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        
        {filteredClients.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            {clients.length === 0 ? 'No clients found' : 'No clients match the current type filter'}
          </div>
        )}
      </div>

      {selectedClient && (
        <ClientDetailsModal
          client={selectedClient}
          onTierUpdated={handleTierUpdated}
          onEmailUpdated={handleEmailUpdated}
          onClose={() => setSelectedClient(null)}
        />
      )}

      {/* Create Client Modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-6 border w-full max-w-2xl shadow-lg rounded-lg bg-white">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h3 className="text-xl font-semibold text-gray-900">Add New Client</h3>
                <p className="text-sm text-gray-500 mt-1">Create a new client account</p>
              </div>
              <button
                onClick={() => setModalOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {error && (
              <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-3">
                <p className="text-red-800 text-sm">{error}</p>
              </div>
            )}

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    First Name *
                  </label>
                  <input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="John"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Doe"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Company Name *
                </label>
                <input
                  type="text"
                  value={formData.companyName}
                  onChange={(e) => handleInputChange('companyName', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Acme Corporation"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email *
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="john@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Password *
                </label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="••••••••"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  value={formData.phoneNumber}
                  onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="+1234567890"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Client type *
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {(
                    [
                      { value: 'business' as const, label: 'Business' },
                      { value: 'private' as const, label: 'Private' },
                    ] as const
                  ).map((opt) => {
                    const selected = formData.clientType === opt.value;
                    const isPrivateOption = opt.value === 'private';
                    return (
                      <label
                        key={opt.value}
                        className={`flex items-center gap-2.5 rounded-lg border-2 p-3 transition-colors ${
                          isPrivateOption
                            ? 'cursor-not-allowed border-gray-100 bg-gray-50 opacity-60'
                            : selected
                              ? 'cursor-pointer border-blue-600 bg-blue-50 ring-2 ring-blue-100'
                              : 'cursor-pointer border-gray-200 bg-white hover:border-gray-300'
                        }`}
                      >
                        <input
                          type="radio"
                          name="clientType"
                          className="h-4 w-4 shrink-0 border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
                          checked={selected}
                          disabled={isPrivateOption}
                          onChange={() => handleInputChange('clientType', opt.value)}
                        />
                        <span className="text-sm font-medium text-gray-900">{opt.label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setModalOpen(false)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateClient}
                disabled={creating}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {creating ? 'Creating...' : 'Create Client'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ClientsPage;
